/**
 * Company-Research per-company pipeline contract — E06-F03 (discover → dedup →
 * disambiguate → enrich → verify → score). Shared by api (the stage services +
 * worker + route) and web (the typed api-client + pipeline view).
 *
 * Parity source (1:1 port): docs/architecture.md §7 ("Company Research — пайплайн")
 * + the E06-F03 task spec (status.yaml). Stage-2 (companies / research_* tables)
 * was NEVER deployed to the legacy n8n prod, so the canonical contract is the
 * architecture doc + migration 023 DDL (mirrored in schema.prisma), NOT a live
 * n8n node — there is no legacy SQL/prompt body to copy verbatim for these
 * stages; the prompt *keys* (research_discover / _disambiguate_entity /
 * _verify_about_us / _score_candidate) come from prisma/seed-data.ts and are
 * loaded from `ai_prompts` at runtime (prompts-only-from-DB).
 *
 * INVARIANTS encoded here (authoritative, from the task acceptance criteria):
 *   - stage ∈ STAGES; failed terminal at attempts >= ATTEMPTS_CAP (=3).
 *   - score NUMERIC(5,2) nullable (manual / unscored); grade ∈ A|B|C|null.
 *   - breakdown = 6 weighted criteria, each verdict ∈ pass|partial|fail
 *     (pass=1.0, partial=0.5, fail=0.0).
 *   - evidence keyed by criterion → { verdict, quote, url } (citation-or-reject).
 *   - UNIQUE(request_id, company_id) is the upsert key (stage idempotency).
 *   - extract-division (ADR 2026-06-02): giants are NOT excluded — the matching
 *     division/brand is extracted and scored; include_decision ∈ whole|division|
 *     exclude; entity_type ∈ pure-play|division-of|portfolio-item.
 *   - grading is REQUEST-DRIVEN: services_match is ONE weighted signal, never a
 *     hard gate for Grade A; HARD filters (geo radius / size cap) are binary gates.
 *   - cache TTL 90d on companies.last_enriched_at / last_verified_at + matching
 *     criteria_hash → row jumps straight to enriched / verified.
 *
 * This file is feature-owned. It is re-exported from `@eer/shared` by the serial
 * Wire pass — it MUST NOT edit the shared barrel during parallel work.
 */
import { z } from 'zod';
import { aiRunStatusSchema } from './research-ai-chain.js';
import { priorCollectionSchema } from './research-prior-collection.js';
import { manualCheckMarkSchema } from './research-manual-check.js';
import { runFailureCodeSchema } from '../runFailure.js';
import { UNRESOLVED_COMPANY_NAME } from '../companyDisplay.js';
import { websiteConfirmationSchema } from '../referenceWebsite.js';
import { rowTriageSchema } from './research-triage.js';
// ---------------------------------------------------------------------------
// Stage machine
// ---------------------------------------------------------------------------
/** research_companies.stage values (schema.prisma:427, migration 023). */
export const RESEARCH_STAGES = [
    'discovered',
    'deduped',
    'disambiguated',
    'enriched',
    'verified',
    'scored',
    'failed',
];
export const researchStageSchema = z.enum(RESEARCH_STAGES);
/** The ordered happy-path stages a row walks (excludes the terminal `failed`). */
export const PIPELINE_ORDER = [
    'discovered',
    'deduped',
    'disambiguated',
    'enriched',
    'verified',
    'scored',
];
/**
 * E06-F05-T04 — the LAST per-company stage the AUTOMATIC (free) path may reach.
 *
 * Everything up to and including `deduped` is free (library lookup by domain/name,
 * no LLM, no paid provider). Every stage AFTER it (`disambiguated` → `enriched` →
 * `verified` → `scored`) costs money, so the per-company driver refuses to walk
 * past this stage unless a caller explicitly raises the cap. Paid work is now the
 * separate, click-initiated AI chain (`aiRunStatus`), never the stage machine.
 *
 * PROVENANCE (E12-F06-T01): delivered in commit df9095f, whose message reads
 * `feat(E06-F05-T05)`; reverting that commit removes this money guard. See
 * docs/commit-provenance.md.
 */
export const AUTO_PIPELINE_STOP_STAGE = 'deduped';
/**
 * DORMANT (E06-F05-T04): the per-company stages the OLD automatic path used to
 * drive after `deduped`. The code implementing them is retained (see
 * `researchCompanyWorker.processCompanyRow`), but nothing reaches them without an
 * explicit `stopAfterStage` opt-in. Kept as documentation of what was disabled.
 */
export const LEGACY_PAID_PIPELINE_STAGES = [
    'disambiguated',
    'enriched',
    'verified',
    'scored',
];
/** The last stage of the FULL legacy per-company pipeline (opt-in only). */
export const LEGACY_PIPELINE_STOP_STAGE = 'scored';
/** Max processing attempts per (request,company) before stage → 'failed' (§4). */
export const ATTEMPTS_CAP = 3;
/** TTL for the companies cache jump (verify/enrich). 90 days (architecture §7). */
export const CACHE_TTL_DAYS = 90;
export const CACHE_TTL_MS = CACHE_TTL_DAYS * 24 * 60 * 60 * 1000;
// ---------------------------------------------------------------------------
// Per-criterion verdict + scoring (request-driven grading, ADR 2026-06-02)
// ---------------------------------------------------------------------------
/** Per-criterion verdict; numeric weight = pass:1.0 / partial:0.5 / fail:0.0. */
export const CRITERION_VERDICTS = ['pass', 'partial', 'fail'];
export const criterionVerdictSchema = z.enum(CRITERION_VERDICTS);
/** Numeric match value per verdict (the weighted-sum coefficients). */
export const VERDICT_MATCH = {
    pass: 1.0,
    partial: 0.5,
    fail: 0.0,
};
/** The 6 scored criteria (mirrors the default research_weight_profile). */
export const SCORE_CRITERIA = [
    'industry',
    'subIndustry',
    'geo',
    'size',
    'founded',
    'services',
];
/**
 * Default weight profile — verbatim from prisma/seed-data.ts / schema.sql
 * (sums to 100). The active profile overrides this at runtime.
 */
export const DEFAULT_WEIGHTS = {
    services: 35,
    subIndustry: 25,
    geo: 15,
    size: 10,
    founded: 10,
    industry: 5,
};
/** research_companies.breakdown — {criterion: pass|partial|fail}. */
export const breakdownSchema = z.record(z.enum(SCORE_CRITERIA), criterionVerdictSchema);
/** One evidence entry per criterion: {verdict, quote, url} (citation-or-reject). */
export const evidenceEntrySchema = z.object({
    verdict: criterionVerdictSchema.or(z.literal('unclear')),
    /** Verbatim snippet from the source (MUST be non-empty for PASS/PARTIAL). */
    quote: z.string(),
    /** Real source URL (MUST be present for PASS/PARTIAL). */
    url: z.string(),
});
/** research_companies.evidence — {criterion: {verdict, quote, url}}. */
export const evidenceMapSchema = z.record(z.string(), evidenceEntrySchema);
// ---------------------------------------------------------------------------
// Normalized criteria (the discovery fan-out + hard-filter input)
// ---------------------------------------------------------------------------
/** A single discovery fan-out tile (sub_industry × geo_tile × size_band). */
export const fanoutTileSchema = z.object({
    subIndustry: z.string().nullable(),
    geoTile: z.string().nullable(),
    sizeBand: z.string().nullable(),
});
/** Binary HARD filters applied as gates during scoring (fail → excluded). */
export const hardFiltersSchema = z
    .object({
    /** Allowed geo centres + radius (km); a hit outside → excluded. */
    geo: z
        .object({
        locations: z.array(z.string()).default([]),
        radiusKm: z.number().nullable().default(null),
        /**
         * E07-F02-T09 — exact radius centres from the intake (a location with
         * lat/lng). Optional: a location without coordinates is geocoded from its
         * label by the offline gazetteer when the radius gate runs.
         */
        centers: z
            .array(z.object({ label: z.string(), lat: z.number(), lng: z.number() }))
            .optional(),
    })
        .optional(),
    /** Local-headcount cap; over → excluded. */
    sizeLocalCap: z.number().nullable().optional(),
    foundedFrom: z.number().nullable().optional(),
    foundedTo: z.number().nullable().optional(),
})
    .passthrough();
/**
 * Output of the `research_criteria_normalize` stage — the canonical, expanded
 * criteria the pipeline consumes (fan-out tiles, grade definitions, DNS, hard
 * filters). `criteriaHash` keys the cache jump.
 */
export const normalizedCriteriaSchema = z.object({
    criteriaHash: z.string(),
    /**
     * The request's primary industry (intake `industry`, E07-F02-T12). Discovery
     * uses its industry-map rule keyword to narrow a generic sub-category
     * ("Computer Software" inside "Biotechnology Research"). Optional — additive.
     */
    industry: z.string().nullable().optional(),
    subIndustries: z.array(z.string()).default([]),
    geoTiles: z.array(z.string()).default([]),
    /**
     * E06-F01-T10 — the search stage of each geoTile (same index; 1 = first). Absent
     * or shorter than geoTiles = stage 1. Discovery searches stage by stage and moves
     * to the next one only while the request target is not reached.
     */
    geoStages: z.array(z.number().int().positive()).optional(),
    sizeBands: z.array(z.string()).default([]),
    fanoutTiles: z.array(fanoutTileSchema).default([]),
    /** Grade-A seed companies (recruiter's `competitors`). */
    competitors: z.array(z.string()).default([]),
    dnsCompanies: z.array(z.string()).default([]),
    dnsIndustries: z.array(z.string()).default([]),
    /**
     * E06-F01-T10 — the brief's Avoid line (business models not to source): cut at
     * discovery by the provider industry and passed to the AI check / scoring prompts.
     */
    avoid: z.array(z.string()).optional(),
    /**
     * E07-F07-T03 — the global Do-Not-Source list (EER clients) as snapshotted at run
     * start: names / domains, kept apart from `dnsCompanies`. A match is cut with reason
     * `dns_global` at discovery, hard gate and grade verdict. Not passed to AI prompts.
     */
    dnsGlobal: z.array(z.string()).optional(),
    hardFilters: hardFiltersSchema.default({}),
    gradeScope: z.enum(['A', 'A+B', 'A+B+C']).default('A+B'),
    gradeDefinitions: z
        .object({ A: z.string().optional(), B: z.string().optional(), C: z.string().optional() })
        .default({}),
    verifyStrictness: z.enum(['strict', 'lenient']).default('strict'),
    /**
     * OVERALL number of companies the recruiter asked to find for THIS request
     * (IntakeCriteria.targetCount, e.g. 52). Passed to the discovery prompt as a
     * volume signal (BUG-11); it is a per-REQUEST target, NOT divided per fan-out
     * tile. Nullable + default null so it is a purely additive field — existing
     * builders/fixtures that omit it stay valid.
     */
    targetCount: z.number().int().positive().nullable().default(null),
});
// ---------------------------------------------------------------------------
// Strategy config (data_source_settings.config — pluggable adapters)
// ---------------------------------------------------------------------------
/** Active strategy config (data_source_settings; seed: ai_discovery_verify). */
export const strategyConfigSchema = z.object({
    /** Adapter keys per stage: { discover, enrich, verify }. */
    adapters: z.object({
        discover: z.string().default('anthropic'),
        enrich: z.string().default('anthropic'),
        verify: z.string().default('anthropic_web_search'),
    }),
    /** Per-queue concurrency for the per-company worker (seed default 8). */
    concurrency: z.number().int().positive().default(8),
    /** web_search budget: max_uses per call, global_budget per run. */
    webSearch: z
        .object({
        maxUses: z.number().int().positive().default(3),
        globalBudget: z.number().int().positive().default(200),
    })
        .default({ maxUses: 3, globalBudget: 200 }),
});
/** Default strategy config — verbatim from seed `ai_discovery_verify`. */
export const DEFAULT_STRATEGY_CONFIG = {
    adapters: { discover: 'anthropic', enrich: 'anthropic', verify: 'anthropic_web_search' },
    concurrency: 8,
    webSearch: { maxUses: 3, globalBudget: 200 },
};
// ---------------------------------------------------------------------------
// Disambiguation (extract-division)
// ---------------------------------------------------------------------------
export const ENTITY_TYPES = ['pure-play', 'division-of', 'portfolio-item'];
export const entityTypeSchema = z.enum(ENTITY_TYPES);
export const INCLUDE_DECISIONS = ['whole', 'division', 'exclude'];
export const includeDecisionSchema = z.enum(INCLUDE_DECISIONS);
/** research_disambiguate_entity output (Sonnet + web_search). */
export const disambiguationResultSchema = z.object({
    entityType: entityTypeSchema,
    includeDecision: includeDecisionSchema,
    /** Name of the extracted matching unit (division/brand/product) or the whole co. */
    resolvedEntity: z.string().nullable(),
    /** Canonical name of the parent conglomerate (for the self-FK), if any. */
    parentName: z.string().nullable(),
    /** Evidence URL — MUST point at the division/product page, not corp root. */
    evidenceUrl: z.string().nullable(),
    confidence: z.number().min(0).max(1),
});
// ---------------------------------------------------------------------------
// Verify (citation-or-reject)
// ---------------------------------------------------------------------------
/** research_verify_about_us per-criterion result before anti-hallucination guard. */
export const verifyCriterionRawSchema = z.object({
    criterion: z.string(),
    verdict: criterionVerdictSchema.or(z.literal('unclear')),
    quote: z.string().nullable().optional(),
    url: z.string().nullable().optional(),
});
export const verifyResultSchema = z.object({
    evidence: evidenceMapSchema,
    confidence: z.number().min(0).max(1),
    needsHuman: z.boolean(),
});
// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------
export const GRADES = ['A', 'B', 'C'];
export const gradeSchema = z.enum(GRADES);
/** research_score_candidate per-candidate result (one of a 20-30 batch). */
export const scoreResultSchema = z.object({
    /** breakdown: {criterion: pass|partial|fail}. */
    breakdown: breakdownSchema,
    /** 0..100 weighted match (null when excluded/unscored). */
    pctMatch: z.number().min(0).max(100).nullable(),
    /** A|B|C|null (null = excluded or below Grade-C cut). */
    grade: gradeSchema.nullable(),
    /** True when a HARD gate excluded the row (geo / size cap). */
    excluded: z.boolean(),
    confidence: z.number().min(0).max(1),
    needsHuman: z.boolean(),
    /** One-line rationale tied to the recruiter's grade definition. */
    rationale: z.string(),
});
// ---------------------------------------------------------------------------
// Pipeline row + worker DTOs
// ---------------------------------------------------------------------------
/** A discovered candidate (discovery fan-out output → research_companies row). */
export const candidateSchema = z.object({
    rawName: z.string(),
    rawDomain: z.string().nullable(),
    /** Grade-A seed (came from criteria.competitors). */
    isSeed: z.boolean().default(false),
    tile: fanoutTileSchema.nullable().optional(),
});
/** The per-company worker job payload (BullMQ). */
export const companyJobSchema = z.object({
    requestId: z.number().int().positive(),
    /** research_companies.id (the long-list row to advance). */
    researchCompanyId: z.number().int().positive(),
});
/** A pipeline row projected for the SPA pipeline view (typed api-client). */
export const pipelineRowSchema = z.object({
    id: z.number(),
    companyId: z.number().nullable(),
    /**
     * E07-F04-T09 — the server-resolved display name (`companies.canonicalName` →
     * rawName → rawDomain → the explicit `UNRESOLVED_COMPANY_NAME` marker). Mirrors
     * `reviewRowSchema.name`: the raw columns go NULL once a row resolves to a
     * Company, and the AI-check screen used to fall back to the row's primary key
     * ("row 4711"), i.e. it showed a database id where a company name belongs.
     * Defaulted so a payload from an older API build still parses mid-deploy.
     */
    name: z.string().default(UNRESOLVED_COMPANY_NAME),
    /** E07-F04-T09 — the server-resolved website: `companies.domain` → rawDomain → null. */
    domain: z.string().nullable().default(null),
    /**
     * E06-F03-T14 — a reference company's website confirmation (see
     * `reviewRowSchema.websiteConfirmation`): the AI-check screen warns on
     * `unconfirmed` before a paid run (never blocks). Optional (older payloads).
     */
    websiteConfirmation: websiteConfirmationSchema.nullable().optional(),
    rawName: z.string().nullable(),
    rawDomain: z.string().nullable(),
    stage: researchStageSchema,
    grade: gradeSchema.nullable(),
    score: z.number().nullable(),
    breakdown: z.record(z.string(), z.unknown()),
    evidence: z.record(z.string(), z.unknown()),
    confidence: z.number().nullable(),
    needsHuman: z.boolean(),
    rationale: z.string().nullable(),
    attempts: z.number(),
    lastError: z.string().nullable(),
    // --- Research AI-chain (scrape-first + Haiku) — per-company MANUAL run state.
    // Additive: nullable/defaulted so existing rows & fixtures that omit them stay
    // valid. SEPARATE from `stage` (discovery machine). See research-ai-chain.ts.
    aiStatus: aiRunStatusSchema.default('idle'),
    aiStartedAt: z.string().nullable().default(null), // ISO
    aiFinishedAt: z.string().nullable().default(null), // ISO
    /**
     * E06-F05-T08 — the HUMAN sentence for a failed run ("The company website blocked
     * automated access — …"), produced by `describeRunFailure` from the raw message
     * persisted in `research_companies.ai_run_error`. This is the field the UI prints;
     * it is no longer the raw technical string (`GET … → HTTP 403`), which now lives in
     * `aiErrorDetail`. Older bundles that still print `aiError` therefore also get a
     * readable text instead of a status line.
     */
    aiError: z.string().nullable().default(null),
    /** Machine-readable cause of the failure — branch on this, never on the wording. */
    aiErrorCode: runFailureCodeSchema.nullable().default(null),
    /** The raw technical message (status/URL). Shown behind a "details" disclosure only. */
    aiErrorDetail: z.string().nullable().default(null),
    /**
     * E07-F04-T12 — the company was already checked in an earlier request (date, link,
     * reused or not). null otherwise; defaulted so older payloads parse.
     */
    priorCollection: priorCollectionSchema.nullable().default(null),
    /**
     * E06-F05-T16 — set when a person entered the check data by hand (the site blocked
     * the AI check): who, when, which page. null otherwise; defaulted so older payloads parse.
     */
    manualCheck: manualCheckMarkSchema.nullable().default(null),
    /**
     * E07-F11-T04 — the Jev triage of the row (same shape as `reviewRowSchema.triage`): the
     * AI-check screen preselects the «check first» bucket. null/absent = not triaged.
     */
    triage: rowTriageSchema.nullable().optional(),
});
/** E06-F05-T16 — POST /research/:requestId/companies/:rowId/manual-check → the rescored row. */
export const manualCheckResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    row: pipelineRowSchema,
});
export const pipelineListResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    rows: z.array(pipelineRowSchema),
});
export const pipelineRunResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    researchCompanyId: z.number(),
    stage: researchStageSchema,
});
//# sourceMappingURL=research-pipeline.js.map